import { Immutable } from "immer";
import React from "react";
import { PDPChangeRootProductBlock } from "tsi-common-react/src/apps/configurator/PDPChangeRootProductBlock/index";
import { PDPChangeVariantOptionBlock } from "tsi-common-react/src/apps/configurator/PDPChangeVariantOptionBlock/index";
import { PDPSelectedRootProductConditionalBlock } from "tsi-common-react/src/apps/configurator/PDPSelectedRootProductConditionalBlock/index";
import { PDPSelectedVariantConditionalBlock } from "tsi-common-react/src/apps/configurator/PDPSelectedVariantConditionalBlock/index";
import { ProductCompareGridSSR } from "tsi-common-react/src/apps/product-compare/ProductCompareGridSSR/index";
import { FeaturesBlock } from "tsi-common-react/src/components/FeaturesBlock/index";
import { TabCardsBlock } from "tsi-common-react/src/components/TabCardsBlock/index";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { IOptionCode } from "tsi-common-react/src/models/catalogue";
import { isoProductID } from "tsi-common-react/src/models/nominals";
import * as streamfieldModels from "tsi-common-react/src/models/streamfield-blocks";
import { check } from "tsi-common-react/src/models/utils";

import { templates } from "@reactivated";
import type {
    CarouselModule as CarouselModuleValue,
    ChangeRootOptionBlock as ChangeRootOptionBlockValue,
    ChangeVariantOptionBlock as ChangeVariantOptionBlockValue,
    FullReviewsModule as FullReviewsModuleValue,
    ProductTable as ProductTableValue,
    SelectedRootProductConditionalBlock as RootConditionalBlockValue,
    TabCardsBlock as TabCardsBlockValue,
    SelectedVariantConditionalBlock as VariantConditionalBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { CarouselModule } from "../blocks/CarouselModule";
import { FeaturesModule } from "../blocks/FeaturesModule";
import { FullReviewsModule } from "../blocks/FullReviewsModule";
import { SealyProductTable } from "../blocks/SealyProductTable";
import { toolboxComponentsMap } from "../blocks/toolboxComponentsMap";

/**
 * Shared assembly for the consolidated sealy ProductDisplayPage /
 * ProductLinePage / ProductLineCollectionPage bodies
 * (``pages.pdp.PDPModuleAssembly``, #33590; PLC via ``wrapBlocks``, #33285). This
 * dispatches the page ``modules`` StreamField -- the hero chrome above the body
 * (``ProductHeroAssemblyTemplate`` + ``{% modular_configurator %}`` + the
 * special-offer badge island, #33582) is rendered separately and is NOT part of
 * this assembly. ``ProductLinePage.configurator_based_modules`` is OUT of scope
 * (configurator-segment-served, never template-rendered; toppers precedent).
 *
 * The base block set comes from ``toolboxComponentsMap`` (the 30 sealy Toolbox
 * keys, including the recursive ``promo_conditional_block`` and the
 * SSR-envelope grids). Layered on top are the PDP module keys and the
 * configurator-driven keys. SEALY KEY -> CLASS deltas, verified against the
 * generated ``PDPModuleAssembly`` union in ``@reactivated`` (the features-key
 * trap: three different keys resolve to two different feature components):
 *
 *   - ``features_block``     -> sealy ``FeaturesModule`` (components/pdp.py),
 *                               NOT the shared FeaturesBlock.
 *   - ``features_block_new`` -> shared ``FeaturesBlock`` (components/pdp.py).
 *   - ``future_block``       -> shared ``FeaturesBlock`` (pages/pdp.py).
 *   - ``carousel``           -> sealy ``CarouselModule`` (Batch A port).
 *   - ``reviews``            -> sealy ``FullReviewsModule`` (page-level reviews
 *                               via the id-keyed side-channel below).
 *   - ``product_table``      -> ``SealyProductTable`` (same dispatch as the
 *                               ProductPage body assembly).
 *   - ``compare_grid``       -> ``ProductCompareGridSSR`` (SSR envelope; plain
 *                               spread, matching this site's
 *                               ProductCompareGridBlockTemplate.tsx re-export,
 *                               which passes NO extra props -- unlike stearns'
 *                               showFirstAvailablePrice/isMinimal).
 *
 * GALLERY: the legacy body loops on BOTH pages skipped top-level
 * ``gallery_block`` (`{% if block.block_type != "gallery_block" %}`; the PDP
 * gallery lives in ``page.product_hero``, #33572), so callers must pass a
 * top-level stream value with ``gallery_block`` filtered OUT (see the page
 * templates). The map keeps the toolbox ``gallery_block`` entry because a
 * gallery nested inside conditional ``content`` DID render on the legacy
 * content-segments path. KNOWN PARITY GAP (deliberate): a nested gallery uses
 * the serialized block-level ``initial_gallery_index`` (block initial_variant
 * -> default gallery -> 0), NOT the legacy page-priority chain (URL variant ->
 * block FK -> page FK from build_gallery_block_ssr_context). Net behavior is
 * better than legacy -- the legacy nested gallery sat inside an inert content
 * segment and never hydrated -- but if full parity is ever needed, thread a
 * page-derived index via the get_pdp_block_ssr side-channel like
 * tab_cards_block does.
 *
 * Five blocks are page-dependent: the reactivated body-serialization path
 * threads ``request`` but not ``page``, so their server-computed SSR props are
 * threaded via id-keyed side-channels (``reviews`` / ``changeVariantSSR`` /
 * ``changeRootSSR`` / ``rootConditionalSSR`` / ``variantConditionalSSR``, built
 * by the ``get_pdp_reviews_props`` / ``get_pdp_block_ssr`` templatetags) and
 * looked up here by ``block.id``, plus two page-level scalars
 * (``pageNumReviews`` for the reviews CTA gate and
 * ``tabCardsInitialRootProductID`` for the tab-cards override). The legacy
 * shared ``pdp-change-variant-option-block.html`` renders BOTH the change-ROOT
 * selector AND the change-VARIANT selector inside its
 * ``#pdp-change-variant-block`` wrapper, so a single
 * ``change_variant_option_block`` carries TWO id-keyed side-channel entries
 * (``changeRootSSR`` + ``changeVariantSSR``) keyed by the same ``block.id``;
 * the standalone stearns-style ``change_root_option_block`` fills
 * ``changeRootSSR`` alone.
 */
/**
 * The page-derived SSR side-channels + scalars the component map closes over.
 * Split out from `IProps` so the styleguide block-render registry can build the
 * same map with minimal (zeroed) props (the fixture path threads no page).
 */
type IMapProps = Immutable<{
    reviews: {
        [k: string]: templates.ProductReviewsTemplate;
    };
    pageNumReviews: number;
    changeVariantSSR: {
        [k: string]: templates.ChangeVariantOptionBlockTemplate;
    };
    changeRootSSR: {
        [k: string]: templates.ChangeRootProductBlockTemplate;
    };
    rootConditionalSSR: {
        [k: string]: templates.SelectedRootProductConditionalBlockTemplate;
    };
    variantConditionalSSR: {
        [k: string]: templates.SelectedVariantConditionalBlockTemplate;
    };
    tabCardsInitialRootProductID: number;
}>;

type IProps = IMapProps &
    Immutable<{
        value: NonNullable<
            templates.ProductLinePageTemplate["page"]["modules"]
        >["value"];
        /**
         * ProductLineCollectionPage mode (#33285): the legacy PLC body loop
         * hand-wrapped every top-level module in
         * `<div class="block-{{ block.block_type }}">` (NO Wagtail `w-block-`
         * class -- the wrapper was written in the page template, unlike
         * GenericPage's `include_block self.body`). The sealy SCSS targets those
         * wrappers (`.block-gallery_block`, `#product-detail > .block-accordion`
         * et al.), so the PLC body island reproduces one per top-level block.
         * The PDP/PLP legacy loops were bare (`React.Fragment`), so they leave
         * this unset. Nested conditional `content` always renders unwrapped
         * (both legacy paths rendered it without per-block wrappers).
         */
        wrapBlocks?: boolean;
    }>;

/**
 * Build the PDP `modules` component map, closing over the page-derived SSR
 * side-channels in `props`. The recursive conditional entries capture the
 * `components` binding by reference (assigned before any entry is invoked), so
 * nested `content` dispatches through this same map -- exactly as when the map
 * was a function-local literal inside `ProductModulesBody`.
 */
export const buildProductModulesComponentsMap = (props: IMapProps) => {
    const components = {
        ...toolboxComponentsMap,

        // `carousel` -> sealy CarouselModule (Batch A).
        carousel: (block: { value: Immutable<CarouselModuleValue> }) => (
            <CarouselModule value={block.value} />
        ),

        // `features_block` -> sealy FeaturesModule. CRITICAL: in
        // PDPModuleAssembly this key is the sealy FeaturesModule
        // (components/pdp.py), NOT the shared FeaturesBlock -- those are the
        // `features_block_new` / `future_block` keys below.
        features_block: (block: {
            value: Immutable<templates.FeaturesModuleTemplate["value"]>;
        }) => <FeaturesModule value={block.value} />,

        // `features_block_new` + `future_block` -> shared FeaturesBlock
        // (self-contained `value` dispatch; stearns `features_block` precedent).
        features_block_new: FeaturesBlock,
        future_block: FeaturesBlock,

        // `product_table` -> SealyProductTable (same entry as the ProductPage
        // body assembly).
        product_table: (block: { value: Immutable<ProductTableValue> }) => (
            <SealyProductTable value={block.value} />
        ),

        // `compare_grid` -> ProductCompareGridSSR (SSR envelope serialized as
        // `block.value`; null outside a request). Plain spread, mirroring the
        // sealy ProductCompareGridBlockTemplate.tsx re-export (no extra props).
        // NOTE (cross-site parity gap, carried over deliberately from the
        // stearns/toppers `product_compare_grid` entries): the legacy
        // `product-compare-grid-block.html` wrapped the island in `<section
        // class="product-compare-grid-block {{theme}} {{spacing}}">`, which the
        // envelope serialization cannot rebuild; fixing it for all three sites
        // means enriching the shared envelope serializer.
        compare_grid: (block: {
            value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
        }) => (block.value ? <ProductCompareGridSSR {...block.value} /> : null),

        // `reviews` -> sealy FullReviewsModule (the Phase-1 #33480 component:
        // `.pdp-section` wrapper, `#reviews` SSR island gated on
        // `reviews_brand_id`, CTA gated on `show_cta && page_num_reviews > 0`).
        // The SSR props are looked up per block by `block.id` from the id-keyed
        // `props.reviews` side-channel (no `max_num` cap on the StreamBlock, so
        // each reviews block carries its own props); a missing entry mirrors
        // the legacy `{% if page.reviews_brand_id %}` gate (the component
        // renders only the empty wrapper when `reviews` is null).
        reviews: (block: {
            id?: string;
            value: Immutable<FullReviewsModuleValue>;
        }) => (
            <FullReviewsModule
                value={block.value}
                reviews={(block.id ? props.reviews[block.id] : null) ?? null}
                page_num_reviews={props.pageNumReviews}
            />
        ),

        // `tab_cards_block` -> shared TabCardsBlock container. Same wrapper /
        // decode as the toolbox map entry (see its docstring), but with the
        // REAL page-derived `initialRootProductID` (threaded from the
        // `get_pdp_block_ssr` tag, which mirrors the legacy shared
        // `TabCardsBlock.get_context` computation) instead of the toolbox
        // entry's hardcoded 0 -- the PDP override the toolbox docstring calls
        // for (#33590).
        tab_cards_block: (block: { value: Immutable<TabCardsBlockValue> }) => {
            const tabCards = check(
                streamfieldModels.TabCardsBlock.decode(block.value),
            );
            return (
                <section
                    className={getGlobalSpacingClasses(block.value.spacing)}
                >
                    <TabCardsBlock
                        tabCards={tabCards}
                        theme={tabCards.theme.theme || ""}
                        initialRootProductID={isoProductID.wrap(
                            props.tabCardsInitialRootProductID,
                        )}
                    />
                </section>
            );
        },

        // `change_variant_option_block` -> reproduces the legacy shared
        // `pdp-change-variant-option-block.html` wrapper
        // (`#pdp-change-variant-block` + optional `|safe` title) around BOTH
        // the change-ROOT and the change-VARIANT selectors, in the legacy
        // order (title -> root -> variant). Both selectors' SSR props are
        // keyed by the SAME `block.id`: the root from `changeRootSSR`, the
        // variant from `changeVariantSSR`. PDPChangeRootProductBlock
        // self-guards (renders null when < 2 root products), so it is a no-op
        // on single-root pages. (Toppers `change_variant_option_block`
        // precedent.)
        change_variant_option_block: (block: {
            id?: string;
            value: Immutable<ChangeVariantOptionBlockValue>;
        }) => {
            const ssr = block.id ? props.changeVariantSSR[block.id] : undefined;
            const rootSSR = block.id
                ? props.changeRootSSR[block.id]
                : undefined;
            const optionInputType = block.value.optionInputType ?? "";
            return (
                <div
                    tabIndex={0}
                    id="pdp-change-variant-block"
                    className={`pdp-change-variant-option-block l-capped-width ${optionInputType}`}
                >
                    {block.value.title && (
                        <h2
                            className="pdp-change-variant-option-block__title"
                            dangerouslySetInnerHTML={{
                                __html: block.value.title,
                            }}
                        />
                    )}
                    {rootSSR && (
                        <PDPChangeRootProductBlock
                            inputType={rootSSR.input_type}
                            initialRootProductsData={
                                rootSSR.initial_root_products_data
                            }
                            initialSelectedRootProductId={
                                rootSSR.initial_selected_root_product_id
                            }
                        />
                    )}
                    {ssr && (
                        <PDPChangeVariantOptionBlock
                            attributeCode={ssr.attribute_code as IOptionCode}
                            inputType={ssr.input_type}
                            initialOptionValuesData={
                                ssr.initial_option_values_data
                            }
                            initialSelectedValue={ssr.initial_selected_value}
                        />
                    )}
                </div>
            );
        },

        // `change_root_option_block` -> reproduces the legacy shared
        // `pdp-change-root-option-block.html` wrapper (`#pdp-change-root-block`
        // + optional `|safe` title -- whose h2 class is the legacy template's
        // own `pdp-change-variant-option-block__title`) around
        // PDPChangeRootProductBlock. SSR props come from the id-keyed
        // `changeRootSSR` side-channel. (Stearns `change_root_option_block`
        // precedent; this key lives on `BasicPDPModuleAssembly`, so it can also
        // appear nested inside conditional content.)
        change_root_option_block: (block: {
            id?: string;
            value: Immutable<ChangeRootOptionBlockValue>;
        }) => {
            const ssr = block.id ? props.changeRootSSR[block.id] : undefined;
            const optionInputType = block.value.optionInputType ?? "";
            return (
                <div
                    tabIndex={0}
                    id="pdp-change-root-block"
                    className={`pdp-change-root-option-block l-capped-width ${optionInputType}`}
                >
                    {block.value.title && (
                        <h2
                            className="pdp-change-variant-option-block__title"
                            dangerouslySetInnerHTML={{
                                __html: block.value.title,
                            }}
                        />
                    )}
                    {ssr && (
                        <PDPChangeRootProductBlock
                            inputType={ssr.input_type}
                            initialRootProductsData={
                                ssr.initial_root_products_data
                            }
                            initialSelectedRootProductId={
                                ssr.initial_selected_root_product_id
                            }
                        />
                    )}
                </div>
            );
        },

        // `root_product_conditional_block` -> PDPSelectedRootProductConditional
        // Block. The legacy `pdp-selected-root-product-conditional-block.html`
        // adds no wrapper (the container emits its own gated tabpanel div), so
        // dispatch directly. The gating props come from the id-keyed
        // `rootConditionalSSR` side-channel; the block's `content` (a
        // `RootProductConditionalBlockContent` StreamBlock =
        // BasicPDPModuleAssembly + a nested `variant_conditional_block`) is
        // rendered as real React children through this same `components` map
        // (#33285) -- replacing the legacy JSON `content_segments` /
        // `dangerouslySetInnerHTML` path so every nested block hydrates.
        // `components` is captured by reference (define-then-close-over),
        // exactly like the recursive `promo_conditional_block`.
        root_product_conditional_block: (block: {
            id?: string;
            value: Immutable<RootConditionalBlockValue>;
        }) => {
            const ssr = block.id
                ? props.rootConditionalSSR[block.id]
                : undefined;
            if (!ssr) {
                return null;
            }
            return (
                <PDPSelectedRootProductConditionalBlock
                    blockProductID={isoProductID.wrap(ssr.block_product_id)}
                    isInitiallyVisible={ssr.is_initially_visible}
                >
                    <StreamField
                        tagName={React.Fragment}
                        value={block.value.content ?? []}
                        components={components}
                    />
                </PDPSelectedRootProductConditionalBlock>
            );
        },

        // `variant_conditional_block` -> PDPSelectedVariantConditionalBlock.
        // Same children-recursion mechanism as the root conditional (the
        // shared container accepts `children`, the toppers #33593 extension);
        // its `content` is a `BasicPDPModuleAssembly` (no further
        // conditionals, but it CAN hold `reviews` / `change_root_option_block`,
        // which resolve in this map via their id-keyed side-channels).
        variant_conditional_block: (block: {
            id?: string;
            value: Immutable<VariantConditionalBlockValue>;
        }) => {
            const ssr = block.id
                ? props.variantConditionalSSR[block.id]
                : undefined;
            if (!ssr) {
                return null;
            }
            return (
                <PDPSelectedVariantConditionalBlock
                    attributeCode={ssr.attribute_code as IOptionCode}
                    attributeValue={ssr.attribute_value}
                    isInitiallyVisible={ssr.is_initially_visible}
                >
                    <StreamField
                        tagName={React.Fragment}
                        value={block.value.content ?? []}
                        components={components}
                    />
                </PDPSelectedVariantConditionalBlock>
            );
        },
    };
    return components;
};

export const ProductModulesBody = (props: IProps): React.JSX.Element => {
    const components = buildProductModulesComponentsMap(props);
    if (props.wrapBlocks) {
        // PLC `.block-{type}` wrapper per top-level block (see IProps doc);
        // the GenericPage per-child StreamField pattern, minus `w-block-`.
        return (
            <>
                {props.value.map((block) => (
                    <StreamField
                        key={block.id}
                        tagName="div"
                        attrs={{ className: `block-${block.type}` }}
                        value={[block]}
                        components={components}
                    />
                ))}
            </>
        );
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.value}
            components={components}
        />
    );
};
